<template>
  <div class="d-flex justify-center story-container">
    <div class="story-info-container pa-2 py-4 pa-sm-6">
      <v-row v-if="loading">
        <v-col>
          <v-skeleton-loader type="image" max-height="100%" height="100%" />
        </v-col>
        <v-col>
          <v-skeleton-loader type="article@3" />
        </v-col>
        <v-col>
          <v-skeleton-loader type="image" max-height="100%" height="100%" />
        </v-col>
      </v-row>
      <v-row v-else-if="!story || !isPublished">
        <v-col class="d-flex align-center justify-center">
          <h1>{{ $t("story.notFound") }}</h1>
        </v-col>
      </v-row>
      <v-row
        v-else
        no-gutters
        class="fill-height align-center align-md-stretch flex-column flex-md-row"
      >
        <v-col class="col-auto" style="min-width: 290px">
          <v-img
            v-if="story.coverArt"
            :src="story.coverArt.contentUrl"
            aspect-ratio="1"
            class="story-cover-art"
          />
          <CoverImagePlaceholder v-else />
        </v-col>
        <v-col
          class="pl-6 pr-4 py-4 py-md-0 d-flex flex-column justify-space-between"
        >
          <div class="basic-info">
            <h2 class="stories__title__alt">
              {{ story.name }}
            </h2>
            <StoryCityGradientButton
              large
              class="save-changes-button my-5 button-above-gradient"
              @click="navigateToFirstChapter"
            >
              {{ $t("chapter.startYourAdventureNow") }}
            </StoryCityGradientButton>
            <div class="mb-1">
              <v-rating
                v-if="story?.reviews?.totalCount > 0"
                class="rating ml-n1"
                hover
                dense
                length="5"
                size="22"
                color="starFilled"
                background-color="#B6B9B8"
                empty-icon="mdi-star"
                readonly
                :value="getAverageRating()"
              />
              <span v-else>
                {{ $t("storyHub.publishing.noReviews") }}
              </span>
            </div>
            <div
              class="story-description description-container__description"
              v-html="story?.description"
            />
            <div class="fade-gradient" />
          </div>
          <div>
            <div class="d-flex flex-wrap" style="gap: 4px">
              <StoryCityInfoChip color="chipRed" class="flex-grow-1 py-1">
                <template v-if="story?.genre" v-slot:icon>
                  <!-- TODO: Genre icons should eventually be unique to the specific genre of the story, need additional designs icons for this -->
                  <!-- Githuib Issue: https://story-city.atlassian.net/browse/SC-1581 -->
                  <v-icon color="red" size="24">$genre</v-icon>
                </template>
                <template v-slot:text>
                  {{
                    story?.genre
                      ? story.genre._id
                      : $t("storyHub.publishing.noGenre")
                  }}
                </template>
              </StoryCityInfoChip>
              <StoryCityInfoChip color="primaryLight" class="flex-grow-1 py-1">
                <template v-slot:icon>
                  <v-icon
                    color="textOnPrimary"
                    size="12"
                    style="margin-top: 3px"
                  >
                    $locationPin
                  </v-icon>
                </template>
                <template v-slot:text>
                  <span class="text--textOnPrimary">
                    {{ readableLocation }}
                  </span>
                </template>
              </StoryCityInfoChip>
            </div>
            <div class="creator-container mt-6 px-4 py-3">
              <Creator :creatorId="story?.owner?.id" />
            </div>
          </div>
        </v-col>
        <v-col class="col-auto flex-shrink-1" style="position: relative">
          <v-img
            :src="require('@/assets/phone_frame.webp')"
            class="phone-frame"
          />
          <div class="qr-code-container">
            <qrcode-vue
              :value="pageLink"
              :size="180"
              class="qr-code"
              level="H"
            />
            <div class="qr-info pa-2">
              <span>
                {{ $t("story.qrCode") }}
              </span>
            </div>
            <div class="d-flex mt-4" style="gap: 10px">
              <IosStoreLink small />
              <GooglePlayStoreLink small />
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import iris from "@/iris";
import hasLoggedInUser from "@/mixins/hasLoggedInUser";
import { STORY_STATUS, APP_UNIVERSAL_LINK, STORY_PATH } from "@/const";
import { gql } from "graphql-tag";
import IosStoreLink from "@/components/IosStoreLink.vue";
import GooglePlayStoreLink from "@/components/GooglePlayLink.vue";
import StoryCityInfoChip from "@/components/StoryCityInfoChip.vue";
import CoverImagePlaceholder from "@/components/CreatorTools/CoverImagePlaceholder";
import Creator from "@/components/User/Creator.vue";
import QrcodeVue from "qrcode.vue";
import useLocationDetails from "@/mixins/useLocationDetails";
import { getAverageRating } from "@/helpers/reviews";
import { isMobile, isIosOrMac } from "@/helpers/devices";
import StoryCityGradientButton from "@/components/Buttons/StoryCityGradientButton.vue";

export default {
  mixins: [hasLoggedInUser, useLocationDetails],
  name: "PurchaseStory",
  data() {
    return {
      tempStoryInLibrary: false,
      dataUrl: null,
      readableLocation: this.$t("unknown"),
    };
  },
  components: {
    IosStoreLink,
    GooglePlayStoreLink,
    StoryCityInfoChip,
    CoverImagePlaceholder,
    Creator,
    StoryCityGradientButton,
    QrcodeVue,
  },
  apollo: {
    story: {
      query: gql`
        query Story($id: ID!) {
          story(id: $id) {
            id
            name
            summary
            description
            priceCents
            status {
              id
              _id
            }
            genre {
              id
              _id
            }
            startChapter {
              id
              place {
                id
                longitude
                latitude
              }
            }
            socialLink {
              id
              image {
                id
                contentUrl
              }
            }
            coverArt {
              id
              contentUrl
            }
            reviews {
              totalCount
              edges {
                node {
                  rating
                }
              }
            }
            owner {
              id
            }
          }
        }
      `,
      variables() {
        return {
          id: this.idWithIri,
        };
      },
    },
  },
  computed: {
    storyId() {
      return this.$route.params.id;
    },
    storyIri() {
      return iris.story;
    },
    idWithIri() {
      return this.storyIri + this.storyId;
    },
    pageLink() {
      return window.location.href;
    },
    loading() {
      return this.$apollo.queries.story.loading;
    },
    isPublished() {
      return this.story?.status?._id === STORY_STATUS.Published;
    },
  },
  watch: {
    story() {
      if (this.story?.startChapter?.place) {
        this.getReadableLocation();
      }
    },
  },
  mounted() {
    if (isMobile() && !isIosOrMac()) {
      window.location.href = APP_UNIVERSAL_LINK + STORY_PATH + this.storyId;
    }
  },
  methods: {
    onDataUrlChange(dataUrl) {
      this.dataUrl = dataUrl;
    },
    getAverageRating() {
      return getAverageRating(this.story?.reviews);
    },
    async getReadableLocation() {
      if (this.story?.startChapter?.place) {
        const localityDetails = await this.getLocality(
          this.story.startChapter.place
        );
        this.readableLocation = `${localityDetails?.city}, ${localityDetails?.country}`;
      }
    },
    navigateToFirstChapter() {
      this.$router.push(`/preview-story/${this.storyId}${this.story.startChapter.id}`);
    },
  },
};
</script>

<style scoped lang="scss">
@import "~vuetify/src/styles/styles.sass";
@import "@/assets/styles/story-container.scss";

.story-description,
.qr-info {
  font-family: DarkerGrotesque, sans-serif;
}

// Story-specific styles that aren't shared
.story-info-container .col {
  height: 464px;
}

.story-cover-art {
  height: 464px;
  width: 290px;
  border-radius: 10px 0px 0px 10px;
}

.basic-info {
  position: relative;
  flex-grow: 1;
  overflow: hidden;
}

::v-deep h2.stories__title__alt {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.story-description {
  font-size: 18px;
  line-height: 24px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: clip;
}

.fade-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 1) 90%
  );
}

.phone-frame {
  height: 464px;
  width: 252px;
  filter: drop-shadow(0px 10px 10px rgba(0, 0, 0, 0.1));
}

.qr-code-container {
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 80px;
  width: 180px;
  margin: auto;
}

.qr-info {
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  text-align: center;
  color: var(--v-textOnPrimary-base);
  background-color: var(--v-primaryLight-base);
  border-radius: 8px;
  margin: 8px 2px 0px;
}

::v-deep .rating .mdi-star::before {
  filter: drop-shadow(0px 5px 5px rgba(0, 0, 0, 0.1));
}

.creator-container {
  border-radius: 20px;
  box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.1);
  width: fit-content;
}

.button-above-gradient {
  position: relative;
  z-index: 2;
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {

  .story-cover-art {
    border-radius: 10px;
  }

  .fade-gradient {
    height: 80px; /* Slightly shorter gradient on mobile */
  }
}
</style>
